import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bookmark, ChevronLeft, Clock, ExternalLink, Gauge, Hand, Droplets, RefreshCw, Share2, Sparkles, Wand2 } from "lucide-react";

import { api, openExternal, track } from "@/lib/api";
import { titleCase } from "@/lib/options";
import { ForYouBadge } from "@/components/cards";
import { AddRoutineButton, LiveBuyBox, LiveImage } from "@/components/shopui";
import { Button, Card, IconCircle, Spinner, Tag } from "@/components/kit";
import { TutorialVideo } from "@/components/widgets";
import { Footer } from "@/pages/ScanResults";
import { useShop } from "@/shop";
import { useApp } from "@/store";

function ProductMatchCard({ pick, label, testId }) {
  const { live } = useShop();
  const navigate = useNavigate();
  const lp = live(pick.slug);
  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <button type="button" data-testid={`${testId}-open`} onClick={() => navigate(`/product/${pick.slug}`)} className="focus-ring flex gap-3 rounded-xl text-left">
        <span className="w-[110px] shrink-0"><LiveImage live={lp} name={pick.name} height={110} /></span>
        <span className="flex flex-1 flex-col gap-1">
          <Tag label={label} tone={label === "Primary" ? "navy" : "sky"} />
          <span className="t-title">{lp?.title ?? pick.name}</span>
          {pick.reason ? <span className="t-caption !text-ink">{pick.reason}</span> : null}
        </span>
      </button>
      <LiveBuyBox slug={pick.slug} testPrefix={`${testId}-buy`} />
      <div className="flex flex-col gap-1.5 rounded-xl bg-tert p-3">
        <span className="t-label !text-navy">How much</span>
        <p className="t-body !text-navy">{pick.amount}</p>
        <span className="t-label mt-1 !text-navy">How to apply</span>
        {pick.application.map((a, i) => <p key={i} className="t-body !text-navy">{i + 1}. {a}</p>)}
      </div>
    </div>
  );
}

export default function StyleDetail() {
  const { slug } = useParams();
  const { profile, saved, toggleSaved, toast, topSlugs } = useApp();
  const [style, setStyle] = useState(null);
  const [match, setMatch] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setError(null);
      const r = await api(`/hairstyles/${slug}/match`, { method: "POST", body: { profile } });
      setStyle(r.style);
      setMatch(r.match);
    } catch (e) {
      setError(e.message);
    }
  }, [slug, profile]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (slug) track("style_view", { slug });
    window.scrollTo({ top: 0 });
  }, [slug]);

  const isSaved = !!slug && saved.includes(slug);
  const onSave = async () => {
    setBusy(true);
    try {
      const now = await toggleSaved(slug);
      toast(now ? "Saved to My Style." : "Removed from My Style.", "success");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/explore", { replace: true }));

  if (error || !style || !match) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6">
        {error ? (
          <>
            <p data-testid="style-error" className="t-body">{error}</p>
            <Button testId="style-retry" title="Retry" variant="outline" onClick={load} />
            <Button testId="style-error-back" title="Go back" variant="ghost" onClick={back} />
          </>
        ) : <Spinner />}
      </div>
    );
  }

  const meta = [
    [Clock, `${match.estimated_time} min`], [Gauge, titleCase(style.difficulty)], [RefreshCw, `${titleCase(style.maintenance)} upkeep`],
    [Hand, `${titleCase(style.hold)} hold`], [Droplets, `${titleCase(style.finish)} finish`],
  ];
  return (
    <div data-testid="style-detail-screen" className="pb-28">
      <div className="mx-auto max-w-6xl md:grid md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] md:gap-10 md:px-6 md:pt-6">
        <div className="relative h-[420px] overflow-hidden bg-navy md:sticky md:top-24 md:h-[calc(100vh-140px)] md:max-h-[720px] md:rounded-[20px]">
          <img src={style.image_url} alt={style.name} className="absolute inset-0 h-full w-full object-cover" />
          <div className="scrim absolute inset-x-0 bottom-0 h-[65%]" />
          <div className="absolute inset-x-4 top-4 flex justify-between">
            <IconCircle testId="style-back-button" tone="glass" icon={ChevronLeft} label="Back" onClick={back} />
            <IconCircle testId="style-save-icon" tone="glass" icon={Bookmark} label={isSaved ? "Remove from saved" : "Save style"} onClick={onSave} />
          </div>
          <div className="absolute inset-x-5 bottom-8 flex flex-col gap-2">
            <div className="flex flex-wrap gap-1.5">{style.tags.slice(0, 3).map((t) => <Tag key={t} tone="glass" label={t.replace("-", " ")} />)}</div>
            <h1 data-testid="style-name" className="font-display text-[38px] leading-10 text-white md:text-5xl">{style.name.toUpperCase()}</h1>
          </div>
        </div>
        <div className="relative -mt-5 rounded-t-[20px] bg-off p-5 md:mt-0 md:p-0">
          <p className="t-body">{style.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {meta.map(([Icon, text]) => (
              <span key={text} className="flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-2 font-body-semi text-xs text-ink"><Icon className="h-3.5 w-3.5 text-navy" aria-hidden />{text}</span>
            ))}
          </div>
          <div className="mt-4 flex gap-3">
            <Button testId="style-tryon-button" title="Try it on" icon={Wand2} variant="secondary" onClick={() => navigate(`/try-on?slug=${style.slug}`)} className="flex-1" />
            <Button testId="style-share-match" title="Share" icon={Share2} variant="outline" onClick={() => navigate(`/share?slug=${style.slug}`)} className="flex-1" />
          </div>
          {topSlugs.has(style.slug) ? <div className="mt-3"><ForYouBadge testId="style-for-you" /></div> : null}

          <h2 className="t-h1 mt-8">YOUR BREEZ MATCH.</h2>
          <Card testId="breez-match-card" className="mt-3 flex flex-col gap-6">
            {match.primary ? <ProductMatchCard testId="match-primary-product" label="Primary" pick={match.primary} /> : <p data-testid="match-no-product" className="t-body">No BREEZ product is currently available for this style — check back soon.</p>}
            {match.supporting ? (
              <>
                <div className="h-px bg-divider" />
                <ProductMatchCard testId="match-supporting-product" label="Supporting · optional" pick={match.supporting} />
              </>
            ) : null}
            <p className="t-caption">{match.note}</p>
            {match.primary ? <AddRoutineButton slugs={[match.primary.slug, match.supporting?.slug].filter(Boolean)} testId="style-add-routine" /> : null}
          </Card>

          <h2 className="t-h1 mt-10">STYLE IT.</h2>
          <div className="mt-2 flex gap-2"><Tag label={`≈ ${match.estimated_time} min`} /><Tag label={`${match.supporting ? 2 : 1} product${match.supporting ? "s" : ""}`} /></div>
          <ol data-testid="style-it-steps" className="mt-3 flex flex-col gap-3">
            {style.styling_steps.map((s, i) => (
              <li key={i} data-testid={`style-step-${i + 1}`} className="flex items-start gap-3 rounded-xl border border-line bg-white p-3">
                <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-navy font-display-bold text-sm text-white">{i + 1}</span>
                <span className="t-body flex-1">{s}</span>
              </li>
            ))}
          </ol>
          <Card className="mt-6 flex flex-col gap-3">
            <div><p className="t-label">Products</p><p className="t-strong mt-1">{[match.primary?.name, match.supporting?.name].filter(Boolean).join(" + ") || "—"}</p></div>
            <div><p className="t-label">Tools</p><p className="t-strong mt-1">{style.required_tools.join(" · ")}</p></div>
          </Card>
          <TutorialVideo url={style.video_url} />
          {match.tips.length ? (
            <div className="mt-6 flex flex-col gap-2">
              <h2 className="t-h2">Styling tips</h2>
              {match.tips.map((t, i) => <p key={i} className="flex gap-2"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-ocean" aria-hidden /><span className="t-body flex-1">{t}</span></p>)}
            </div>
          ) : null}
        </div>
      </div>
      <Footer>
        <Button testId="style-save-button" title={isSaved ? "Saved" : "Save"} icon={Bookmark} variant="outline" loading={busy} onClick={onSave} className="flex-1" />
        <Button testId="style-shop-button" title="breez.au" icon={ExternalLink} onClick={() => openExternal("https://breez.au")} className="flex-[1.6]" />
      </Footer>
    </div>
  );
}
